import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { Center, Column, Screen, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { BackButton, StepIndicator } from '~/commonComponents';
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { useGetProfessionalCountsByOffice } from '~/domain/Professionals/usecases/useGetProfessionalCountsByOffice';
import { useProfessionOptions } from '~/hooks';
import { useUserSelector } from '~/hooks/useUserSelector';
import { fireDraftReminderEmailLinkClicked } from '~/posthogs/events';
import { NumberUtils } from '~/utils/NumberUtils';
import { pluralize } from '~/utils/String';
import { formatProfessionalCount } from '../utils';
import {
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageLocationState,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import { useCurrentPermanentJobPostingSlug, useDraftJobPost } from './hooks';

export const CreatePermanentJobsPage = () => {
  const location = useLocation();
  const slug = useCurrentPermanentJobPostingSlug();
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const navigate = useNavigate();

  const { draft, isLoading } = useDraftJobPost(id);

  const user = useUserSelector();
  const {
    countsByProfession,
    isLoading: isCountLoading,
    isError: isCountError,
  } = useGetProfessionalCountsByOffice(user?.id, { distance: 25 });

  const { findByCode } = useProfessionOptions();
  const professionType = draft?.professionType ?? '';
  const professionTypeLabel = findByCode(professionType)?.label ?? '';
  const totalCount = countsByProfession
    ? (countsByProfession[professionType as keyof typeof countsByProfession] ?? 0)
    : 0;
  const showCount = !isCountLoading && !isCountError && totalCount > 0;

  useEffect(() => {
    if (isLoading || !draft) return;

    if (draft.status !== JobPostingStatus.Draft) {
      navigate('/', { replace: true });
    }
  }, [draft, isLoading, navigate]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('utm_source') === 'email' && params.get('utm_medium') === 'draft_reminder') {
      fireDraftReminderEmailLinkClicked({ tier: params.get('utm_content') ?? undefined });
    }
  }, [location.search]);

  // Review & Payment is shared with the Copy & Paste import flow (Import → Confirm
  // Details → Payment). When entered from there, show that 3-step stepper instead of
  // the manual 4-step one.
  const isFromImport = Boolean(
    (location.state as TCreatePermanentJobPostingPageLocationState | null)?.fromImport,
  );

  const steps = useMemo(() => {
    if (isFromImport) {
      return ['Import', 'Confirm Details', 'Payment'];
    }
    return ['Job Information', 'Work Details', 'Description', 'Payment'];
  }, [isFromImport]);

  const currentStepIndex = useMemo(() => {
    if (isFromImport) {
      // Import (0) → Confirm Details (1) → Payment (2)
      return 2;
    }
    switch (slug) {
      case CreatePermanentJobPostingSteps.BasicInformation:
        return 0;
      case CreatePermanentJobPostingSteps.WorkDetails:
        return 1;
      case CreatePermanentJobPostingSteps.JobDescription:
        return 2;
      case CreatePermanentJobPostingSteps.ReviewAndPayment:
        return 3;
      default:
        return 0;
    }
  }, [slug, isFromImport]);

  // The funnel header is contextual: step 1 asks what they're hiring for, then once
  // the professional count is known it flips to a count-led nudge to finish the post.
  const header = useMemo<{ title?: string; subtitle?: string }>(() => {
    const countLabel = pluralize(professionTypeLabel, totalCount);

    // Indexed by step (0–3), matching currentStepIndex.
    const headersByStep: { title?: string; subtitle?: string }[] = [
      // 0 · Job Information
      {
        title: "Let's See Who's In Your Area",
        subtitle:
          "Tell us what you're hiring for and we'll show you how many professionals are near you.",
      },
      // 1 · Work Details — once the count is known, lead with it
      {
        title: showCount
          ? `${NumberUtils.applyThousandSeparator(totalCount)} ${countLabel} in Your Area`
          : 'Dental Professionals Near You Are on GoTu',
        subtitle:
          "Just set your schedule and compensation and we'll get your job in front of them.",
      },
      // 2 · Job Description
      {
        title: 'One More Step to Receive Applicants',
        subtitle: showCount
          ? // eslint-disable-next-line max-len
            `Add your job description and your role goes out to ${formatProfessionalCount(totalCount)} ${pluralize(professionTypeLabel.toLowerCase(), totalCount)}.`
          : // eslint-disable-next-line max-len
            `Add your job description and your role goes out to ${professionTypeLabel.toLowerCase()} in your area.`,
      },
      // 3 · Review & Payment
      {
        title: "Now Let's Tell Them You're Hiring",
        subtitle: 'Once you post, qualified professionals are notified and can apply immediately.',
      },
    ];

    return headersByStep[currentStepIndex] ?? {};
  }, [currentStepIndex, showCount, totalCount, professionTypeLabel]);

  return (
    <Screen variant="standard">
      <Screen.Header start={<BackButton />}>
        {/*
          StepIndicator renders below the back-button row, not in the `title`
          slot: the DS Stepper is full-width and the header's `start` slot is
          positioned `absolute left-0`, so a full-width title overlaps the back
          button. Matches the ShiftRequestHeader pattern.
        */}
        <Center>
          {/* Constrain so the full-width DS Stepper doesn't span the whole header. */}
          <Column style={{ width: '66%' }}>
            <StepIndicator steps={steps} currentStep={currentStepIndex} />
          </Column>
        </Center>

        {(header.title || header.subtitle) && (
          <Center>
            <Column align="center" gap={2} style={{ maxWidth: 633 }}>
              {header.title && (
                <Typography
                  as="span"
                  bold
                  size="heading_2"
                  color={colors.neutral[600]}
                  textAlign="center"
                >
                  {header.title}
                </Typography>
              )}
              {header.subtitle && (
                <Typography
                  as="span"
                  variant="body1"
                  color={colors.neutral[500]}
                  textAlign="center"
                >
                  {header.subtitle}
                </Typography>
              )}
            </Column>
          </Center>
        )}
      </Screen.Header>
      <Screen.Body>
        <Column gap={0} style={{ paddingLeft: 28, paddingRight: 28, paddingBottom: 28 }}>
          <Outlet />
        </Column>
      </Screen.Body>
    </Screen>
  );
};
